/**
 * ⭐ F228 — A KEY FOR THE CALENDAR CHIP'S MARKS.
 *
 * > Pete, 2026-10-01 (a red mark on a chip): *"What does the red icon mean again? There is no
 * > where to find out?"* → popup *"Both, Reporting first"* (the Reporting restyle, then this).
 *
 * Every mark has a hover on the chip itself, but nothing on screen said where to look. This is
 * a small (i) at the end of the legend row; hover, focus or click opens the key. The marks are
 * drawn by the SAME components the chips use (the work marks), or the same icon in the same
 * pill (driver, paperwork, licence), on a chip-coloured swatch — so the key cannot drift from
 * what the calendar draws.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlarmClockOff, FileText, ScanLine, Truck } from 'lucide-react';
import { LaborPulse, LaborPausedMark, LaborDoneMark } from '@/components/AppointmentHoverCard';

const PILL = 'inline-flex items-center gap-0.5 shrink-0 rounded-full bg-black/45 px-1 py-0.5 text-white ring-1 ring-white/25';

/** What a chip mark means — one row each, in the order they sit on a chip. */
export const CALENDAR_ICON_KEY: Array<{ key: string; mark: React.ReactNode; title: string; text: string }> = [
    { key: 'noshow', title: 'Possible no-show',
      mark: <span className="inline-flex shrink-0 items-center rounded bg-red-600 p-0.5 text-white ring-1 ring-white/60"><AlarmClockOff className="h-3 w-3" aria-hidden="true" /></span>,
      text: 'The slot has ended (past the no-show grace) and no arrival is recorded. Mark it from Possible no-shows in the header.' },
    { key: 'working', title: 'Being worked', mark: <LaborPulse />, text: 'Loading or unloading is in progress.' },
    { key: 'paused', title: 'Paused', mark: <LaborPausedMark />, text: 'A worker is assigned and nobody is on it right now.' },
    { key: 'done', title: 'Work finished', mark: <LaborDoneMark />, text: 'Loaded / unloaded, the truck has not departed yet. A wide chip shows a LOADED or UNLOADED stamp instead.' },
    { key: 'driver', title: 'Driver', mark: <span className={PILL}><Truck className="h-3 w-3" aria-hidden="true" /></span>, text: 'A driver is assigned or checked in; a number = more than one.' },
    { key: 'paper', title: 'Paperwork', mark: <span className={PILL}><FileText className="h-3 w-3" aria-hidden="true" /><span className="pr-0.5 text-[10px] font-bold leading-none">2</span></span>, text: 'Documents on file for this appointment (how many).' },
    { key: 'licence', title: 'License', mark: <span className={PILL}><ScanLine className="h-3 w-3" aria-hidden="true" /></span>, text: "The driver's license is on file." },
];

export function CalendarIconKey() {
    const [open, setOpen] = useState(false);
    const [pinned, setPinned] = useState(false);
    const ref = useRef<HTMLButtonElement>(null);
    const [box, setBox] = useState<{ left: number; top: number } | null>(null);

    const place = () => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        const W = 340;
        setBox({ left: Math.max(8, Math.min(r.left, window.innerWidth - W - 8)), top: Math.min(r.bottom + 6, window.innerHeight - 380) });
    };
    const show = () => { place(); setOpen(true); };
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setPinned(false); setOpen(false); } };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    return (
        <>
            <button
                ref={ref}
                type="button"
                aria-label="What the marks on an appointment mean"
                aria-expanded={open}
                title="What the marks on an appointment mean"
                data-calendar-icon-key=""
                onMouseEnter={show}
                onMouseLeave={() => { if (!pinned) setOpen(false); }}
                onFocus={show}
                onBlur={() => { setPinned(false); setOpen(false); }}
                onClick={() => { if (pinned) { setPinned(false); setOpen(false); } else { setPinned(true); show(); } }}
                className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-stone-300 font-serif text-[11px] italic text-stone-500 hover:border-stone-500 hover:text-stone-900"
            >
                i
            </button>
            {open && box && createPortal(
                <div
                    style={{ left: box.left, top: box.top, width: 340 }}
                    className="fixed z-50 rounded-lg border bg-background p-3 shadow-2xl"
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => { if (!pinned) setOpen(false); }}
                >
                    <div className="mb-2 text-xs font-semibold">The marks on an appointment</div>
                    <ul className="space-y-1.5">
                        {CALENDAR_ICON_KEY.map((k) => (
                            <li key={k.key} className="flex items-start gap-2 text-[11px] leading-snug" data-icon-key={k.key}>
                                <span className="flex h-6 w-9 shrink-0 items-center justify-center rounded bg-status-approved">{k.mark}</span>
                                <span><b className="font-semibold">{k.title}</b> — {k.text}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-2 border-t pt-2 text-[10px] text-muted-foreground">
                        The chip's colour is its status (the legend row). IN / OUT is the direction. Hover any mark on a chip for its own note.
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
}
